/**
 * React Query hooks for System Tenants Management
 * 
 * These hooks access system-level endpoints (admin only)
 * that don't require tenant context.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rawApi } from '@/lib/api-client';
import type { 
  SystemTenant, 
  SystemTenantsResponse, 
  TenantConfig, 
  TenantUsage,
  CreateTenantInput,
  UpdateTenantConfigInput,
} from '../types';

// Query keys
const SYSTEM_TENANTS_KEY = ['system', 'tenants'];

// =============================================================================
// System Tenants List
// =============================================================================

interface UseSystemTenantsParams {
  skip?: number;
  limit?: number;
  search?: string;
}

export function useSystemTenants(params?: UseSystemTenantsParams) {
  return useQuery({
    queryKey: [...SYSTEM_TENANTS_KEY, params],
    queryFn: async () => {
      const response = await rawApi.get<SystemTenantsResponse>('system/tenants', {
        params: {
          skip: params?.skip ?? 0,
          limit: params?.limit ?? 20,
          q: params?.search || undefined,
        },
      });
      return response.data;
    },
  });
}

// =============================================================================
// Tenant Config
// =============================================================================

export function useTenantConfig(tenantId: string | null) {
  return useQuery({
    queryKey: [...SYSTEM_TENANTS_KEY, tenantId, 'config'],
    queryFn: async () => {
      if (!tenantId) throw new Error('Tenant ID required');
      const response = await rawApi.get<TenantConfig>(`system/tenants/${tenantId}/config`);
      return response.data;
    },
    enabled: !!tenantId,
  });
}

export function useUpdateTenantConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, data }: { tenantId: string; data: UpdateTenantConfigInput }) => {
      const response = await rawApi.patch<TenantConfig>(
        `system/tenants/${tenantId}/config`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_TENANTS_KEY });
      queryClient.invalidateQueries({ queryKey: [...SYSTEM_TENANTS_KEY, tenantId] });
    },
  });
}

// =============================================================================
// Tenant Usage
// =============================================================================

export function useTenantUsage(tenantId: string | null) {
  return useQuery({
    queryKey: [...SYSTEM_TENANTS_KEY, tenantId, 'usage'],
    queryFn: async () => {
      if (!tenantId) throw new Error('Tenant ID required');
      const response = await rawApi.get<TenantUsage>(`system/tenants/${tenantId}/usage`);
      return response.data;
    },
    enabled: !!tenantId,
  });
}

// =============================================================================
// Create Tenant
// =============================================================================

export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTenantInput) => {
      const response = await rawApi.post<SystemTenant>(
        'system/tenants',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_TENANTS_KEY });
      queryClient.invalidateQueries({ queryKey: ['system', 'dashboard'] });
    },
  });
}
